// src/lib/theme-bridge.ts
import { generatePrimaryVariants, hexToHSLString, getContrastColor } from './color-utils';
import { hexToHSL } from './utils';
import { THEME_COLOR_DEFAULTS } from 'constants/themeDefaults';
import { logger } from 'utils/logger';

/**
 * Interface for Strapi theme colors
 * Compatible with the existing white-label system at /public/assets/theme/colors.json
 */
export interface StrapiThemeColors {
  background: {
    primary: string;
    secondary: string;
    tertiary?: string;
  };
  text: {
    primary: string;
    secondary: string;
    tertiary?: string;
  };
  primary: string;
  secondary?: string;
  accent?: string;
  success?: string;
  warning?: string;
  info?: string;
  error?: string;
  border?: string;
  ring?: string;
}

/**
 * Default fallback colors (used ONLY if Strapi does not provide them)
 * Uses centralized theme defaults as the single source of truth.
 */
const FALLBACK_COLORS = {
  success: THEME_COLOR_DEFAULTS.success,
  warning: THEME_COLOR_DEFAULTS.warning,
  info: THEME_COLOR_DEFAULTS.info,
  error: THEME_COLOR_DEFAULTS.error,
} as const;

/**
 * Adjusts the brightness of a hex color
 */
export const adjustBrightness = (hex: string, percent: number): string => {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  const adjust = (value: number) => {
    const adjusted = value + (value * percent) / 100;
    return Math.min(255, Math.max(0, Math.round(adjusted)));
  };

  const toHex = (value: number) => value.toString(16).padStart(2, '0');

  return `#${toHex(adjust(r))}${toHex(adjust(g))}${toHex(adjust(b))}`;
};

/**
 * Apply primary color and its variants based on current mode
 * This is called separately from neutral colors to allow dynamic primary updates
 */
export const applyPrimaryColorVariants = (primaryHex: string, isDark: boolean): void => {
  const root = document.documentElement;
  const variants = generatePrimaryVariants(primaryHex, isDark);

  // Primary and variants
  root.style.setProperty('--primary', hexToHSLString(variants.primary));
  root.style.setProperty('--primary-foreground', hexToHSLString(variants.primaryForeground));
  root.style.setProperty('--primary-hover', hexToHSLString(variants.primaryHover));
  root.style.setProperty('--primary-active', hexToHSLString(variants.primaryActive));
  root.style.setProperty('--primary-muted', hexToHSLString(variants.primaryMuted));

  // Ring uses primary
  root.style.setProperty('--ring', hexToHSLString(primaryHex));

  // Info uses primary by default
  root.style.setProperty('--info', hexToHSLString(primaryHex));

  logger.debug('[Theme Bridge] Primary variants applied:', {
    primary: primaryHex,
    isDark,
    variants,
  });
};

/**
 * Converts Strapi theme (colors.json) to shadcn/ui CSS variables
 * Now accepts isDark to apply the correct variants
 *
 * NOTE: Does not apply the .dark class - that is the responsibility of useThemeMode
 *
 * @param theme Object with Strapi theme colors
 * @param isDark Whether dark mode is active
 */
export const applyStrapiThemeToShadcn = (
  theme: StrapiThemeColors,
  isDark: boolean = true
): void => {
  // Validate required properties
  if (!theme.background?.primary || !theme.background?.secondary) {
    throw new Error(
      '[Theme Bridge] Propriedades obrigatórias de background faltando no tema Strapi'
    );
  }
  if (!theme.text?.primary || !theme.text?.secondary) {
    throw new Error('[Theme Bridge] Propriedades obrigatórias de text faltando no tema Strapi');
  }
  if (!theme.primary) {
    throw new Error('[Theme Bridge] Propriedade obrigatória primary faltando no tema Strapi');
  }

  const root = document.documentElement;

  // ====== PRIMARY COLOR VARIANTS (mode-aware) ======
  applyPrimaryColorVariants(theme.primary, isDark);

  // ====== STATUS COLORS (same for both modes) ======
  const successColor = theme.success || FALLBACK_COLORS.success;
  const warningColor = theme.warning || FALLBACK_COLORS.warning;
  const infoColor = theme.info || theme.primary; // Info defaults to primary
  const errorColor = theme.error || FALLBACK_COLORS.error;

  root.style.setProperty('--success', hexToHSL(successColor));
  root.style.setProperty('--warning', hexToHSL(warningColor));
  root.style.setProperty('--destructive', hexToHSL(errorColor));
  root.style.setProperty('--destructive-foreground', hexToHSL(getContrastColor(errorColor)));

  // ====== CHART COLORS (derived from brand + semantic) ======
  const accentColor = theme.accent || adjustBrightness(theme.primary, -12);
  root.style.setProperty('--chart-1', hexToHSL(theme.primary));
  root.style.setProperty('--chart-2', hexToHSL(successColor));
  root.style.setProperty('--chart-3', hexToHSL(warningColor));
  root.style.setProperty('--chart-4', hexToHSL(infoColor));
  root.style.setProperty('--chart-5', hexToHSL(errorColor));
  root.style.setProperty('--chart-6', hexToHSL(accentColor));
  root.style.setProperty('--chart-7', hexToHSL(adjustBrightness(theme.primary, 20)));
  root.style.setProperty('--chart-8', hexToHSL(adjustBrightness(successColor, -10)));

  // Border radius (keep current config)
  root.style.setProperty('--radius', '0.5rem');

  // NOTE: We no longer apply the .dark class here
  // That is the responsibility of the useThemeMode hook

  logger.debug('[Theme Bridge] Strapi theme applied successfully', {
    primaryColor: theme.primary,
    isDark,
    statusColors: {
      success: successColor,
      warning: warningColor,
      info: infoColor,
      error: errorColor,
    },
  });
};

/**
 * Removes the shadcn/ui theme application (useful for cleanup)
 */
export const removeStrapiThemeFromShadcn = (): void => {
  const root = document.documentElement;

  const cssVars = [
    '--primary',
    '--primary-foreground',
    '--primary-hover',
    '--primary-active',
    '--primary-muted',
    '--success',
    '--warning',
    '--info',
    '--destructive',
    '--destructive-foreground',
    '--chart-1',
    '--chart-2',
    '--chart-3',
    '--chart-4',
    '--chart-5',
    '--chart-6',
    '--chart-7',
    '--chart-8',
    '--ring',
    '--radius',
  ];

  cssVars.forEach(varName => root.style.removeProperty(varName));

  logger.debug('[Theme Bridge] shadcn/ui theme removed');
};
